import { useEffect } from 'react';
import { SOCIAL_CARD } from './brand';

/**
 * Inject a JSON-LD <script type="application/ld+json"> block into <head>.
 * Cleans itself up on unmount/dependency change so the schema only applies
 * while the relevant page is mounted.
 */
export const useJsonLd = (data, id = 'jsonld') => {
  useEffect(() => {
    if (!data) return undefined;
    const scriptId = `jsonld-${id}`;
    const existing = document.getElementById(scriptId);
    const tag = existing || document.createElement('script');
    tag.id = scriptId;
    tag.type = 'application/ld+json';
    tag.text = JSON.stringify(data);
    if (!existing) document.head.appendChild(tag);
    return () => {
      const t = document.getElementById(scriptId);
      if (t && t.parentNode) t.parentNode.removeChild(t);
    };
  }, [data, id]);
};

export const ORG_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'NewsMediaOrganization',
  '@id': 'https://www.outlawmedia.com.au/#org',
  name: 'Outlaw Media',
  alternateName: 'Outlaw Media AU',
  url: 'https://www.outlawmedia.com.au/',
  logo: {
    '@type': 'ImageObject',
    url: SOCIAL_CARD,
    width: 1600,
    height: 500,
  },
  description:
    "Independent crime news outlet covering Australia's underworld - gangland conflicts, organised crime, and investigations the mainstream media won't touch.",
  sameAs: [
    'https://instagram.com/ryannaumenko',
    'https://x.com/ryan_naumenko',
    'https://youtube.com/@ryan.naumenko',
  ],
  foundingLocation: { '@type': 'Place', name: 'Melbourne, Australia' },
};

export const WEBSITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': 'https://www.outlawmedia.com.au/#website',
  url: 'https://www.outlawmedia.com.au/',
  name: 'Outlaw Media',
  publisher: { '@id': 'https://www.outlawmedia.com.au/#org' },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://www.outlawmedia.com.au/stories?q={search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
};

export const buildStoriesItemList = (stories) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: stories.slice(0, 30).map((s, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    url: s.slug ? `https://www.outlawmedia.com.au/stories/${s.slug}` : s.link,
    name: s.title,
  })),
});

export const BYLINE_PROFILES = {
  'Ryan Naumenko': { type: 'Person', url: 'https://www.outlawmedia.com.au/about' },
  'Staff Writers': { type: 'Organization', url: 'https://www.outlawmedia.com.au/about' },
  'The Roadman': { type: 'Person', url: 'https://www.outlawmedia.com.au/about' },
};

export const buildAuthor = (name) => {
  const byline = name || 'Staff Writers';
  const profile = BYLINE_PROFILES[byline] || BYLINE_PROFILES['Staff Writers'];
  return [{ '@type': profile.type, name: byline, url: profile.url }];
};

export const buildNewsArticle = (story) => {
  const url = story.slug ? `https://www.outlawmedia.com.au/stories/${story.slug}` : story.link;
  const image = story.cover_image_url || story.image;
  return {    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: story.title,
    url,
    image: image ? [image] : undefined,
    datePublished: story.published_at,
    dateModified: story.updated_at || story.published_at,
    author: buildAuthor(story.author),
    publisher: {
      '@type': 'NewsMediaOrganization',
      name: 'Outlaw Media',
      logo: {
        '@type': 'ImageObject',
        url: SOCIAL_CARD,
        width: 1600,
        height: 500,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    description: story.excerpt || story.summary || story.subtitle,
    articleSection: (story.tags && story.tags[0]) || 'Crime',
    keywords: (story.tags || []).join(', ') || undefined,
    inLanguage: 'en-AU',
    // Paywalled reporting. Google requires this pair (isAccessibleForFree
    // false + the paywalled section selector) so indexing a subscriber-only
    // story is treated as flexible sampling, not cloaking.
    isAccessibleForFree: false,
    hasPart: {
      '@type': 'WebPageElement',
      isAccessibleForFree: false,
      cssSelector: '.paywalled-body',
    },
  };
};

/**
 * Inject / update SEO meta tags on <head> for the current page.
 * Handles: title, meta description, canonical, Open Graph, Twitter Card,
 * and article-specific tags (article:published_time, article:author, etc).
 * Cleans up on unmount / dependency change.
 */
export const useSeoMeta = ({
  title,
  description,
  canonical,
  image,
  type = 'article',
  publishedAt,
  modifiedAt,
  author,
  tags,
} = {}) => {
  useEffect(() => {
    if (!title && !description) return undefined;
    const originalTitle = document.title;
    if (title) document.title = title;

    const meta = (attr, value, content) => {
      if (!content) return null;
      let el = document.querySelector(`meta[${attr}="${value}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, value);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return { el, created };
    };

    const link = (rel, href) => {
      if (!href) return null;
      let el = document.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
      return { el, created };
    };

    const created = [];
    const track = (r) => { if (r?.created) created.push(r.el); };

    track(meta('name', 'description', description));
    track(link('canonical', canonical));

    track(meta('property', 'og:type', type));
    track(meta('property', 'og:title', title));
    track(meta('property', 'og:description', description));
    track(meta('property', 'og:url', canonical));
    track(meta('property', 'og:image', image));
    track(meta('property', 'og:site_name', 'Outlaw Media'));
    track(meta('property', 'og:locale', 'en_AU'));

    track(meta('name', 'twitter:card', 'summary_large_image'));
    track(meta('name', 'twitter:title', title));
    track(meta('name', 'twitter:description', description));
    track(meta('name', 'twitter:image', image));

    if (type === 'article') {
      track(meta('property', 'article:published_time', publishedAt));
      track(meta('property', 'article:modified_time', modifiedAt));
      track(meta('property', 'article:author', author));
      track(meta('property', 'article:publisher', 'https://www.outlawmedia.com.au/'));
      if (Array.isArray(tags)) {
        tags.forEach((t) => {
          const el = document.createElement('meta');
          el.setAttribute('property', 'article:tag');
          el.setAttribute('content', t);
          document.head.appendChild(el);
          created.push(el);
        });
      }
    }

    return () => {
      document.title = originalTitle;
      created.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, [title, description, canonical, image, type, publishedAt, modifiedAt, author, tags]);
};

export const buildVideoObject = (video) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: video.title,
  description: video.description || video.title,
  contentUrl: video.video_url,
  thumbnailUrl: video.thumbnail_url ? [video.thumbnail_url] : undefined,
  uploadDate: video.uploaded_at,
  publisher: { '@id': 'https://www.outlawmedia.com.au/#org' },
});

export const buildBreadcrumb = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

/**
 * Mark the current page as noindex for Google/Bing crawlers.
 *
 * We can't return a real HTTP 404 from an SPA, so Google was flagging our
 * NotFound / article-not-found pages as "Soft 404" (200 OK with nothing
 * useful). This hook injects `<meta name="robots" content="noindex, follow">`
 * on mount and removes it on unmount, so those responses tell Google
 * explicitly not to index them (while still following outbound links so
 * PageRank flows back into the site).
 *
 * Pass `active=false` to disable - e.g. an article page that only wants
 * to noindex conditionally when the article failed to load.
 */
export const useNoIndex = (active = true) => {
  useEffect(() => {
    if (!active) return undefined;
    // If a prior page left a robots meta tag behind we still overwrite it;
    // the cleanup handler restores whatever was there originally.
    let el = document.querySelector('meta[name="robots"]');
    const created = !el;
    const previous = el ? el.getAttribute('content') : null;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex, follow');

    return () => {
      if (created) {
        el.parentNode && el.parentNode.removeChild(el);
      } else if (previous !== null) {
        el.setAttribute('content', previous);
      }
    };
  }, [active]);
};
